import { PAGEPATHS } from "components/Common/Constant"
import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { ApiNames } from '../../components/Common/ApiManagement/apiServices'
import axiosInstance from "components/Common/ApiManagement/axiosInstance";
import { setBreadcrumbItems } from "../../store/actions"
import { connect } from "react-redux"
import DataTableWithPopup from "components/longContentTable";
import {
  Mail,
  Shield,
  Info,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Server
} from 'lucide-react'
import "./MailServer.css"

const MailServer = (props) => {
    document.title = "Mail Server";
    // const [scanId, setScanId] = useState(sessionStorage.getItem('scanId'))
    const { scanId: paramScanId } = useParams();
    const [scanId, setScanId] = useState(paramScanId || sessionStorage.getItem('scanId'));
    const [trustMailData, setTrustMailData] = useState({})

    const breadcrumbItems = [
        { title: "Scan List", link: PAGEPATHS.Scanlist },
        { title: sessionStorage.getItem('domain'), link: "#" },
        { title: "Mail Server", link: "#" }
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Mail Server", breadcrumbItems)
    })

    useEffect(() => {
        const getTrustMailData = async () => {
            try {
                const { data } = await axiosInstance.get(`${ApiNames.FETCH_TRUST_MAIL_SCAN}/${scanId}`)
                const valid = Object.entries(data.data).reduce((a, v) => {
                    a.columns.push({
                        label: v[0],
                        field: v[0],
                        sort: 'asc',
                        width: 150
                    })
                    if (typeof v[1] === 'boolean' || v[1] === 0) {
                        a.rows[0][v[0]] = v[1].toString()
                    } else if (v[1] === null) {
                        a.rows[0][v[0]] = 'NaN'
                    } else {
                        a.rows[0][v[0]] = v[1]
                    }
                    return a
                }, { columns: [], rows: [{}] })
                setTrustMailData(valid)
            } catch (e) {
                console.error(e);
            }
        }
        getTrustMailData()
    }, [scanId])

    // Extract key email security metrics
    const getSecurityMetrics = () => {
        if (!trustMailData || !trustMailData.rows || trustMailData.rows.length === 0) {
            return { spf: null, dmarc: null, dkim: null };
        }

        const data = trustMailData.rows[0];
        return {
            spf: data['SPF Valid'] || data['spf_valid'] || null,
            dmarc: data['DMARC Valid'] || data['dmarc_valid'] || null,
            dkim: data['DKIM Valid'] || data['dkim_valid'] || null,
        };
    };

    const metrics = getSecurityMetrics();

    const getStatusInfo = (value) => {
        if (value === null || value === undefined || value === 'NaN') {
            return { icon: AlertTriangle, color: 'warning', text: 'Unknown', class: 'warning' };
        }
        const val = value.toString().toLowerCase();
        if (val === 'true') {
            return { icon: CheckCircle, color: 'success', text: 'Valid', class: 'success' };
        }
        return { icon: XCircle, color: 'error', text: 'Invalid', class: 'error' };
    };

    return (
        <React.Fragment>
            <div className="mail-server-page">
                {/* Page Header */}
                <div className="mail-server-header">
                    <div className="mail-server-header-content">
                        <div className="mail-server-icon-wrapper">
                            <Mail className="mail-server-icon" />
                        </div>
                        <div className="mail-server-header-text">
                            <h1>Email Security Configuration</h1>
                            <p>Email authentication and security validation for {sessionStorage.getItem('domain')}</p>
                        </div>
                    </div>
                </div>

                {/* Info Section */}
                <div className="mail-server-info-box">
                    <div className="mail-server-info-header">
                        <div className="mail-server-info-icon-wrapper">
                            <Info className="mail-server-info-icon" />
                        </div>
                        <h3>About Email Security</h3>
                    </div>
                    <div className="mail-server-info-content">
                        <p>
                            This assessment validates email authentication protocols including SPF, DMARC, and DKIM
                            to ensure your domain's email security and prevent spoofing attacks.
                        </p>
                        <p>
                            Proper email authentication helps protect your domain reputation and ensures legitimate
                            emails are delivered while preventing unauthorized use of your domain.
                        </p>
                    </div>
                </div>

                {/* Security Status Cards */}
                <div className="mail-server-status-grid">
                    {/* SPF Status */}
                    {(() => {
                        const status = getStatusInfo(metrics.spf);
                        const StatusIcon = status.icon;
                        return (
                            <div className={`mail-server-status-card ${status.class}`}>
                                <div className="mail-server-status-icon-wrapper">
                                    <StatusIcon className={`mail-server-status-icon ${status.class}`} />
                                </div>
                                <div className="mail-server-status-content">
                                    <h3>SPF (Sender Policy Framework)</h3>
                                    <div className={`mail-server-status-value ${status.class}`}>
                                        {status.text}
                                    </div>
                                    <p className="mail-server-status-label">
                                        Verifies authorized mail servers
                                    </p>
                                </div>
                            </div>
                        );
                    })()}

                    {/* DMARC Status */}
                    {(() => {
                        const status = getStatusInfo(metrics.dmarc);
                        const StatusIcon = status.icon;
                        return (
                            <div className={`mail-server-status-card ${status.class}`}>
                                <div className="mail-server-status-icon-wrapper">
                                    <StatusIcon className={`mail-server-status-icon ${status.class}`} />
                                </div>
                                <div className="mail-server-status-content">
                                    <h3>DMARC Policy</h3>
                                    <div className={`mail-server-status-value ${status.class}`}>
                                        {status.text}
                                    </div>
                                    <p className="mail-server-status-label">
                                        Email authentication policy
                                    </p>
                                </div>
                            </div>
                        );
                    })()}

                    {/* DKIM Status */}
                    {(() => {
                        const status = getStatusInfo(metrics.dkim);
                        const StatusIcon = status.icon;
                        return (
                            <div className={`mail-server-status-card ${status.class}`}>
                                <div className="mail-server-status-icon-wrapper">
                                    <StatusIcon className={`mail-server-status-icon ${status.class}`} />
                                </div>
                                <div className="mail-server-status-content">
                                    <h3>DKIM (DomainKeys)</h3>
                                    <div className={`mail-server-status-value ${status.class}`}>
                                        {status.text}
                                    </div>
                                    <p className="mail-server-status-label">
                                        Email digital signature
                                    </p>
                                </div>
                            </div>
                        );
                    })()}
                </div>

                {/* Detailed Trust Mail Data */}
                <div className="mail-server-data-section">
                    <div className="mail-server-section-header">
                        <Server className="section-header-icon" />
                        <h2>TrustyMail Security Analysis</h2>
                        <span className="scan-id">Scan ID: {scanId}</span>
                    </div>
                    <div className="mail-server-data-content">
                        {trustMailData && trustMailData.rows && trustMailData.rows.length > 0 ? (
                            <DataTableWithPopup noBottomColumns thirdPartyData={trustMailData} />
                        ) : (
                            <div className="mail-server-empty-state">
                                <Mail className="mail-server-empty-icon" />
                                <p>No TrustyMail data available</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}
export default connect(null, { setBreadcrumbItems })(MailServer)